// OnboardingWizard — contextual modal driven by the F1 onboarding API.
//
// Lifecycle:
//   1. On mount → GET /api/app-memberships/me/onboarding/{app_key}
//        - 404 (no membership yet) → POST /upsert to create pending,
//          then re-fetch onboarding.
//   2. Renders one step per `missing[]` field, ordered hard → soft → optional.
//   3. Each [Next] click → PATCH /me/onboarding/{app_key} with the new field.
//   4. When pct=100 → backend auto-promotes to "active" → success screen.
//
// Wire format:
//   - The PATCH payload is `{ fields: {field: value}, field_states: {field: "done"|"skipped"} }`.
//   - We also send `consent_accepted: true` on the FIRST patch to record consent.
//
// Strict scope: no extra UI for under-18, questionnaires, profile_data,
// catalog or coach collaboration.

import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { useHubI18n } from "@/i18n/hubI18n";
import { Loader2, X, CheckCircle2 } from "lucide-react";

const TIER_ORDER = { hard_required: 0, soft_required: 1, optional: 2 };

// Field type heuristic — minimal mapping for the 4 known F4 fields.
const FIELD_INPUT_TYPE = {
  currency_preference: "select",
  marketing_opt_in: "boolean",
  opt_in_validation_phase: "boolean",
  sport_level: "select",
};
const FIELD_OPTIONS = {
  currency_preference: ["EUR", "USD", "GBP", "ARS", "MXN", "CLP"],
  sport_level: ["beginner", "intermediate", "advanced", "competitive"],
};

const OnboardingWizard = ({ appKey, onClose }) => {
  const { lang } = useLang();
  const i18n = useHubI18n(lang);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missing, setMissing] = useState([]);
  const [skippedLocally, setSkippedLocally] = useState(() => new Set());
  const [completionPct, setCompletionPct] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [firstPatchDone, setFirstPatchDone] = useState(false);

  // ---- Initial load (with auto-upsert on 404) -----------------------------
  const fetchOnboarding = async () => {
    try {
      const r = await api.get(`/app-memberships/me/onboarding/${appKey}`);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 404) {
        // Create a pending membership and try again.
        await api.post("/app-memberships/upsert", {
          app_key: appKey,
          source: "self_signup",
        });
        const r2 = await api.get(`/app-memberships/me/onboarding/${appKey}`);
        return r2.data;
      }
      throw e;
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOnboarding();
        if (cancelled) return;
        const sorted = [...(data.missing || [])].sort(
          (a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9)
        );
        setMissing(sorted);
        setCompletionPct(data.completion_pct || 0);
        if ((data.missing || []).length === 0) setDone(true);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey]);

  const currentField = missing[stepIdx];

  // Reset input when step changes
  useEffect(() => { setValue(""); }, [stepIdx]);

  const inputType = currentField ? (FIELD_INPUT_TYPE[currentField.field] || "text") : null;
  const isValid = useMemo(() => {
    if (!currentField) return false;
    if (inputType === "boolean") return value === true || value === false;
    if (inputType === "select") return Boolean(value);
    return String(value || "").trim().length > 0;
  }, [value, currentField, inputType]);

  const submitField = async (action) => {
    if (!currentField) return;
    setSaving(true);
    try {
      const body = {
        fields: action === "done" ? { [currentField.field]: value } : {},
        field_states: { [currentField.field]: action },
      };
      if (!firstPatchDone) {
        body.consent_accepted = true;
        setFirstPatchDone(true);
      }
      const r = await api.patch(`/app-memberships/me/onboarding/${appKey}`, body);
      setCompletionPct(r.data?.completion_pct || 0);
      const rawMissing = [...(r.data?.missing || [])].sort(
        (a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9)
      );
      // Track locally-skipped fields so soft_required skips don't loop.
      const nextSkipped = new Set(skippedLocally);
      if (action === "skipped") nextSkipped.add(currentField.field);
      setSkippedLocally(nextSkipped);
      setMissing(rawMissing);
      // Filter to "progressable" = not yet locally-skipped this session.
      const progressable = rawMissing.filter((f) => !nextSkipped.has(f.field));
      if (progressable.length === 0) {
        // Either pct=100 (backend already promoted) OR every remaining
        // missing field is soft/optional and has been skipped → treat as done.
        setDone(true);
      } else {
        // Find the next index in the new missing[] list that is NOT
        // locally-skipped. Reset to 0 if past the end.
        const nextIdx = rawMissing.findIndex((f) => !nextSkipped.has(f.field));
        setStepIdx(Math.max(0, nextIdx));
      }
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const close = () => onClose && onClose();

  const appLabel = i18n.appLabels[appKey] || appKey;
  const wizardTitle = (i18n.wizardTitle || "").replace("{{app}}", appLabel);

  // -------- Render ---------------------------------------------------------
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4"
      data-testid="onboarding-wizard-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900" data-testid="onboarding-wizard-title">
            {wizardTitle}
          </h3>
          <button
            type="button"
            onClick={close}
            className="text-slate-400 hover:text-slate-700"
            data-testid="onboarding-wizard-close"
            aria-label={i18n.wizardClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${completionPct}%` }}
            data-testid="onboarding-progress-bar"
            aria-valuenow={completionPct}
          />
        </div>

        {/* Body */}
        <div className="px-5 py-5 min-h-[180px]">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="onboarding-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> {i18n.wizardLoading}
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-600" data-testid="onboarding-error">
              {i18n.wizardError}
            </div>
          )}
          {!loading && !error && done && (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="onboarding-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <p className="text-sm text-slate-700">{i18n.wizardDone}</p>
            </div>
          )}
          {!loading && !error && !done && currentField && (
            <div data-testid={`onboarding-step-${currentField.field}`}>
              <div className="text-[11px] uppercase tracking-wide mb-2">
                {currentField.tier === "hard_required" && (
                  <span className="text-red-600 font-semibold">{i18n.wizardRequired}</span>
                )}
                {currentField.tier === "soft_required" && (
                  <span className="text-amber-600 font-semibold">{i18n.wizardRecommended}</span>
                )}
                {currentField.tier === "optional" && (
                  <span className="text-slate-500 font-semibold">{i18n.wizardOptional}</span>
                )}
              </div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                {i18n.fieldLabels[currentField.field] || currentField.field}
                {currentField.tier === "hard_required" && <span className="text-red-500 ms-1">*</span>}
              </label>
              {inputType === "select" && (
                <select
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid={`onboarding-step-input-${currentField.field}`}
                >
                  <option value="">—</option>
                  {(FIELD_OPTIONS[currentField.field] || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
              {inputType === "boolean" && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setValue(true)}
                    className={`px-3 py-1.5 rounded-md text-sm border ${value === true ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-300 text-slate-700"}`}
                    data-testid={`onboarding-step-input-${currentField.field}-yes`}
                  >
                    {lang === "es" ? "Sí" : "Yes"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue(false)}
                    className={`px-3 py-1.5 rounded-md text-sm border ${value === false ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-300 text-slate-700"}`}
                    data-testid={`onboarding-step-input-${currentField.field}-no`}
                  >
                    {lang === "es" ? "No" : "No"}
                  </button>
                </div>
              )}
              {inputType === "text" && (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid={`onboarding-step-input-${currentField.field}`}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          {done ? (
            <button
              type="button"
              onClick={close}
              className="ms-auto px-4 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition"
              data-testid="onboarding-complete-btn"
            >
              {i18n.wizardClose}
            </button>
          ) : (
            <>
              <div>
                {currentField && currentField.tier === "soft_required" && (
                  <button
                    type="button"
                    onClick={() => submitField("skipped")}
                    disabled={saving}
                    className="text-xs text-slate-600 hover:text-slate-900 underline"
                    data-testid="onboarding-skip-soft-btn"
                  >
                    {i18n.wizardSkipSoft}
                  </button>
                )}
                {currentField && currentField.tier === "optional" && (
                  <button
                    type="button"
                    onClick={() => submitField("skipped")}
                    disabled={saving}
                    className="text-xs text-slate-600 hover:text-slate-900 underline"
                    data-testid="onboarding-skip-optional-btn"
                  >
                    {i18n.wizardSkipOptional}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => submitField("done")}
                disabled={!isValid || saving || loading}
                className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid="onboarding-next-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : i18n.wizardNext}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
